import { createFileRoute, Navigate } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { useState } from 'react'

const MOCK_BASE = 'http://localhost:3003'

export const Route = createFileRoute('/dev/heyreach')({
  component: DevHeyReachPage,
})

interface MockWorkspace {
  workspaceId: number
  name?: string
  leads?: Array<{ linkedInUrl: string; firstName?: string; lastName?: string }>
}

function DevHeyReachPage() {
  if (import.meta.env.MODE !== 'development') {
    return <Navigate to="/app" />
  }
  return <DevHeyReachPanel />
}

function DevHeyReachPanel() {
  const [workspaces, setWorkspaces] = useState<MockWorkspace[]>([])
  const [log, setLog] = useState<Array<{ ts: string; msg: string; ok: boolean }>>([])
  const [loading, setLoading] = useState(false)

  function append(msg: string, ok: boolean) {
    setLog((prev) => [{ ts: new Date().toLocaleTimeString(), msg, ok }, ...prev].slice(0, 50))
  }

  async function call(path: string, body?: unknown) {
    try {
      const res = await fetch(`${MOCK_BASE}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      })
      const text = await res.text()
      append(`POST ${path} → ${res.status} ${text.slice(0, 200)}`, res.ok)
    } catch (e) {
      append(`POST ${path} → ${(e as Error).message}`, false)
    }
  }

  async function refresh() {
    setLoading(true)
    try {
      const res = await fetch(`${MOCK_BASE}/_sim/workspaces`)
      if (!res.ok) {
        append(`GET /_sim/workspaces → ${res.status}`, false)
        setWorkspaces([])
        return
      }
      const data = (await res.json()) as { items?: MockWorkspace[] } | MockWorkspace[]
      const items = Array.isArray(data) ? data : (data.items ?? [])
      setWorkspaces(items)
      append(`Loaded ${items.length} workspace(s) from mock`, true)
    } catch (e) {
      append(`GET /_sim/workspaces → ${(e as Error).message}`, false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-6 py-10">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">HeyReach mock simulator</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Local-only dev panel. Talks directly to the mock server at{' '}
          <code className="text-foreground">{MOCK_BASE}</code>.
        </p>
      </header>

      <div className="flex items-center gap-3">
        <CtaButton size="sm" onClick={refresh} loading={loading}>
          {loading ? 'Loading…' : 'Refresh workspaces'}
        </CtaButton>
      </div>

      {workspaces.length === 0 ? (
        <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-8 text-center text-sm text-muted-foreground">
          No workspaces yet. Enable outreach for a startup, then click "Refresh".
        </div>
      ) : (
        <div className="space-y-4">
          {workspaces.map((w) => (
            <WorkspaceCard key={w.workspaceId} workspace={w} onSim={call} />
          ))}
        </div>
      )}

      <section className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Activity
        </h2>
        <div className="space-y-1 rounded-xl border border-border/60 bg-card/30 p-3 font-mono text-xs">
          {log.length === 0 ? (
            <p className="text-muted-foreground">No calls yet.</p>
          ) : (
            log.map((entry, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: log entries are append-only
              <div key={i} className={entry.ok ? 'text-foreground' : 'text-destructive'}>
                [{entry.ts}] {entry.msg}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  )
}

function WorkspaceCard({
  workspace,
  onSim,
}: {
  workspace: MockWorkspace
  onSim: (path: string, body?: unknown) => Promise<void>
}) {
  const [leadUrl, setLeadUrl] = useState(workspace.leads?.[0]?.linkedInUrl ?? '')
  const [replyText, setReplyText] = useState('Hi — sounds interesting, can we hop on a call?')
  const wsId = workspace.workspaceId

  return (
    <div className="rounded-xl border border-border/60 bg-card/30 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">
            {workspace.name ?? `Workspace ${wsId}`}
          </p>
          <p className="text-xs text-muted-foreground">workspaceId: {wsId}</p>
        </div>
        <div className="flex gap-2">
          <SecondaryButton
            size="sm"
            onClick={() => onSim('/_sim/connect-sender', { workspaceId: wsId })}
          >
            Connect sender
          </SecondaryButton>
          <SecondaryButton
            size="sm"
            onClick={() => onSim('/_sim/disconnect-sender', { workspaceId: wsId })}
          >
            Disconnect sender
          </SecondaryButton>
          <SecondaryButton
            size="sm"
            onClick={() =>
              onSim('/_sim/clone-template', {
                workspaceId: wsId,
                campaignName: 'Investor Outreach v1',
              })
            }
          >
            Clone template
          </SecondaryButton>
        </div>
      </div>

      <div className="space-y-2 border-t border-border/40 pt-3">
        <label className="block text-xs text-muted-foreground">
          Lead LinkedIn URL
          <input
            type="text"
            value={leadUrl}
            onChange={(e) => setLeadUrl(e.target.value)}
            placeholder="https://linkedin.com/in/..."
            className="mt-1 w-full rounded-md border border-border/60 bg-background px-2 py-1 text-sm text-foreground"
          />
        </label>
        <label className="block text-xs text-muted-foreground">
          Reply text
          <input
            type="text"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="mt-1 w-full rounded-md border border-border/60 bg-background px-2 py-1 text-sm text-foreground"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <CtaButton
            size="sm"
            onClick={() =>
              onSim('/_sim/connection-accepted', { workspaceId: wsId, leadLinkedInUrl: leadUrl })
            }
            disabled={!leadUrl}
          >
            Fire connection-accepted
          </CtaButton>
          <CtaButton
            size="sm"
            onClick={() =>
              onSim('/_sim/reply', {
                workspaceId: wsId,
                leadLinkedInUrl: leadUrl,
                text: replyText,
              })
            }
            disabled={!leadUrl}
          >
            Fire reply
          </CtaButton>
          <CtaButton
            size="sm"
            onClick={() =>
              onSim('/_sim/campaign-completed', { workspaceId: wsId, leadLinkedInUrl: leadUrl })
            }
            disabled={!leadUrl}
          >
            Fire campaign-completed
          </CtaButton>
        </div>
      </div>
    </div>
  )
}
